import React, { useCallback, useState, useEffect } from "react";
import { FormattedMessage, WrappedComponentProps, injectIntl } from "react-intl";

import { COLORS, glyphs, Icon } from "@treelab/panda";

import { Input, DropdownDivider } from "@treelab/panda";

import FormItem from "../FormItem";

import { IFormItemChangeValue, IDimensionSelectOption } from "../../../../types/form-type";

import {
  ChildrenMixin,
  OptionContainer,
  ErrorContainer,
  IconContainer,
  ChildrenErrorMixin,
  ChildrenUndefinedMixin,
} from "./styled";

interface IProps extends WrappedComponentProps {
  id: string;
  labelId: string;
  value?: string;
  options: IDimensionSelectOption[];
  onChange: (value: IFormItemChangeValue) => void;
  cancelSelection?: boolean; // 是否允许取消选择
  validates?: {
    checkFunc: (data: any) => boolean;
    errorMessageId: string;
  }[];
  offset?: [number, number];
  showClearIcon?: boolean;
  showClearOption?: boolean;
}

const Select: React.FC<IProps> = ({
  id,
  labelId,
  value,
  options: propsOptions,
  onChange,
  cancelSelection,
  validates,
  intl: { formatMessage },
  offset,
  showClearIcon,
}) => {
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (Array.isArray(validates) && validates.length > 0) {
      let checkError: string = "";
      validates.forEach((item) => {
        const { checkFunc, errorMessageId } = item;
        if (!checkFunc({ value, options: propsOptions, id })) {
          checkError = errorMessageId;
          return;
        }
      });
      setError(checkError);
    }
  }, [value, id, propsOptions, validates]);

  const handleChange = useCallback(
    (data: string) => {
      if (cancelSelection && value === data) {
        onChange({ [id]: "" });
      } else {
        onChange({ [id]: data });
      }
    },
    [onChange, cancelSelection, id, value]
  );

  const handleClickClearIcon = useCallback(
    (event: React.SyntheticEvent) => {
      onChange({ [id]: "" });
      event.preventDefault();
      event.stopPropagation();
    },
    [onChange, id]
  );

  return (
    <FormItem
      labelId={labelId}
      childrenMixin={error ? ChildrenErrorMixin : !value ? ChildrenUndefinedMixin : ChildrenMixin}
      id={id}
    >
      <Input.Tree
        placeholder={formatMessage({ id: "form.placeholder.select.input" })}
        searchPlaceholder={formatMessage({
          id: "form.placeholder.select.tree.search",
        })}
        inputStyle={{
          backgroundColor: COLORS.greyLight5,
          flex: 0.95,
          borderRadius: "8px",
          margin: "8px 5px 8px 5px",
          paddingInlineStart: "inherit",
        }}
        options={propsOptions as IDimensionSelectOption[]}
        input={{
          name: id,
          onChange: handleChange,
          value: value,
        }}
        isRightArrow={false}
        placement={"bottom-end"}
        meta={{}}
        renderGroupTitle={(index: number, options: IDimensionSelectOption[]) => {
          if (index + 1 >= options.length) return null;
          const optionA = options[index];

          if (index === 0) {
            return (
              <OptionContainer>
                <FormattedMessage id={optionA.priorityLevelLabelId} />
              </OptionContainer>
            );
          }

          const optionB = options[index - 1];

          if (index > 0 && optionA.priorityLevelLabelId !== optionB.priorityLevelLabelId) {
            return (
              <OptionContainer>
                <FormattedMessage id={optionA.priorityLevelLabelId} />
              </OptionContainer>
            );
          }
        }}
        renderDiver={(index: number, options: IDimensionSelectOption[]) => {
          if (index + 1 >= options.length) return null;

          const optionA = options[index];
          const optionB = options[index + 1];

          if (optionA.priorityLevelLabelId !== optionB.priorityLevelLabelId) {
            if (index >= 1) {
              return (
                <OptionContainer>
                  <DropdownDivider />
                </OptionContainer>
              );
            }
            return null;
          }
        }}
      />
      {error && (
        <ErrorContainer>
          <FormattedMessage id={error} />
        </ErrorContainer>
      )}
      {showClearIcon && !!value && (
        <IconContainer onClick={handleClickClearIcon}>
          <Icon icon={glyphs.FAILED} size={{ width: 12, height: 12 }} />
        </IconContainer>
      )}
    </FormItem>
  );
};

export default injectIntl(Select);
